import React from "react";
import { Button } from "./ui/button";
import { Typography } from "./ui/typography";
import { ArrowLeft, Clock10, Loader2 } from "lucide-react";
import lockIcon from "../assets/icons/lock.svg";
import eightIcon from "../assets/images/80eight/88-footer.svg";
import clsx from "clsx";

interface QuoteTimerFooterProps {
  timer: number;
  isTimerZero: boolean;
  confirmed: boolean;
  isLoadingQuote: boolean;
  isRefreshing: boolean;
  confirmButtonText: string;
  onBack: () => void;
  onRefresh: () => void;
  onConfirm: () => void;
  dashboard?: boolean;
}

export const QuoteTimerFooter: React.FC<QuoteTimerFooterProps> = ({
  timer,
  isTimerZero,
  confirmed,
  isLoadingQuote,
  isRefreshing,
  confirmButtonText,
  onBack,
  onRefresh,
  onConfirm,
  dashboard = false,
}) => {
  return (
    <>
      <div className="flex flex-col gap-4 w-full px-6 py-6">
        {!isTimerZero ? (
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              onClick={onBack}
              className="hidden md:flex w-10 h-10 min-w-10 max-w-10 rounded-full p-0 items-center justify-center border border-gray-200 hover:bg-gray-100"
            >
              <ArrowLeft size={20} className="text-text-secondary" />
            </Button>
            <div className="text-text-secondary bg-gray-100 p-4 w-full max-w-full md:max-w-xs rounded-full flex items-center justify-center gap-1">
              <Typography className="text-sm text-text-secondary">
                Your quote will expire in:
              </Typography>
              <Clock10
                size={16}
                className="bg-purple-400 rounded-full text-white"
              />
              <Typography className="text-sm font-semibold text-text-primary">
                {timer}s
              </Typography>
            </div>
            <Button
              onClick={onConfirm}
              variant="default"
              disabled={confirmed}
              className={clsx(
                "hidden md:flex items-center justify-center py-6 btn-primary",
                dashboard ? "md:!w-[50%] !w-full" : "w-full"
              )}
            >
              {confirmed ? (
                <div className="flex items-center gap-2">
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Loading...
                </div>
              ) : (
                confirmButtonText
              )}
            </Button>
          </div>
        ) : (
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              onClick={onBack}
              className="hidden md:flex w-10 h-10 min-w-10 max-w-10 rounded-full p-0 items-center justify-center border border-gray-200 hover:bg-gray-100"
            >
              <ArrowLeft size={20} className="text-text-secondary" />
            </Button>
            <Button
              variant="ghost"
              onClick={onRefresh}
              disabled={confirmed || isRefreshing}
              className="p-6 w-full btn-primary flex items-center justify-center"
            >
              {isLoadingQuote ? (
                <div className="flex items-center gap-2">
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Loading...
                </div>
              ) : (
                <Typography className="text-white">Refresh Quote</Typography>
              )}
            </Button>
            <Button
              onClick={onConfirm}
              variant="ghost"
              disabled={true}
              className={clsx(
                "hidden md:flex items-center justify-center py-6 text-text-secondary border border-gray-800 rounded-full",
                dashboard ? "md:!w-[50%] !w-full" : "w-full"
              )}
            >
              {confirmButtonText}
            </Button>
          </div>
        )}

        <div className="flex md:hidden items-center gap-4 w-full">
          <Button
            variant="ghost"
            onClick={onBack}
            className="w-10 h-10 min-w-10 max-w-10 rounded-full p-0 flex items-center justify-center border border-gray-200 hover:bg-gray-100"
          >
            <ArrowLeft size={20} className="text-text-secondary" />
          </Button>
          <Button
            onClick={onConfirm}
            variant={isTimerZero ? "ghost" : "default"}
            disabled={isTimerZero || confirmed}
            className={clsx(
              "py-6 flex items-center justify-center",
              dashboard ? "md:!w-[50%] !w-full" : "w-full",
              isTimerZero
                ? "text-text-secondary border border-gray-800 rounded-full"
                : "btn-primary"
            )}
          >
            {confirmed ? (
              <div className="flex items-center gap-2">
                <Loader2 className="h-4 w-4 animate-spin" />
                Loading...
              </div>
            ) : (
              confirmButtonText
            )}
          </Button>
        </div>
      </div>

      <div className="p-6 mt-6 justify-center border-t border-gray-100">
        <div className="flex items-center justify-center space-x-2">
          <img src={lockIcon} alt="lock" />
          <Typography variant="p" className="text-gray-500">
            Secured by
          </Typography>
          <img src={eightIcon} alt="eight" />
        </div>
      </div>
    </>
  );
};
